import { Capacitor } from '@capacitor/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { readStoredAuth } from '../api/authApi';
import { requestWithFallback } from '../api/httpClient';
import { getEndpointCandidates } from '../config/apiConfig';
import { OFFLINE_STORES,getOfflineStorageDriver } from './offlineStorageAdapter';
import { buildMergeProposal,canonicalMerge,mergeHash } from './visitaMergeJournal';
import { createDeviceSyncProcessor } from './deviceSyncProcessor';

const enabled=()=>import.meta.env?.VITE_DEVICE_SYNC_ENABLED==='true';
const keyName='device_sync_installation_v1';
const b64=bytes=>btoa(String.fromCharCode(...new Uint8Array(bytes)));
const unb64=value=>Uint8Array.from(atob(value),c=>c.charCodeAt(0));
const claims=token=>JSON.parse(new TextDecoder().decode(unb64(token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/'))));
let activeProcessor=null;
let installationTask=null;

function canRenewBinding(secret,currentClaims){
    return Object.values(secret?.bindings||{}).some(binding=>binding.user_id===Number(currentClaims.sub)
        &&binding.empresa_id===Number(currentClaims.empresa_id)&&Number.isSafeInteger(binding.generation)&&binding.generation>0
        &&typeof binding.binding_uuid==='string'&&binding.binding_uuid.length===36);
}

async function installation(allowCreate) {
    if(installationTask)return installationTask;
    installationTask=loadInstallation(allowCreate);
    try{return await installationTask;}finally{installationTask=null;}
}

async function loadInstallation(allowCreate) {
    const old=await SecureStorage.get(keyName);
    if(old)return old;
    if(!allowCreate)return null;
    const keys=await crypto.subtle.generateKey({name:'RSASSA-PKCS1-v1_5',modulusLength:2048,publicExponent:new Uint8Array([1,0,1]),hash:'SHA-256'},true,['sign','verify']);
    const publicBytes=await crypto.subtle.exportKey('spki',keys.publicKey);
    const encoded=b64(publicBytes).match(/.{1,64}/g).join('\n');
    const value={installationUuid:crypto.randomUUID(),publicKey:`-----BEGIN PUBLIC KEY-----\n${encoded}\n-----END PUBLIC KEY-----\n`,privateJwk:await crypto.subtle.exportKey('jwk',keys.privateKey),bindings:{}};
    await SecureStorage.set(keyName,value);
    if(canonicalMerge(await SecureStorage.get(keyName))!==canonicalMerge(value))throw Error('No se confirmó la clave protegida de la instalación.');
    return value;
}

export async function processDeviceSyncRequests({run,isCurrent,newCycle=false}) {
    if(!enabled()||!Capacitor.isNativePlatform())return {disabled:true};
    const session=await readStoredAuth();if(!session?.token||!isCurrent())return {blocked:true};
    const currentClaims=claims(session.token),owner=`${currentClaims.empresa_id}:${currentClaims.sub}:${currentClaims.jti}`;
    let capability;
    try{capability=await requestWithFallback({moduleName:'deviceSync',endpoint:'capabilities',endpointIsKey:true,token:session.token,method:'get'});}
    catch(error){if(error?.response?.status===404)return {disabled:true};throw error;}
    if(!capability.data?.enabled||!isCurrent())return {disabled:true};
    const mayEnroll=capability.data.enrollment===true&&import.meta.env.VITE_DEVICE_SYNC_ENROLLMENT_ENABLED==='true';
    const secret=await installation(mayEnroll);
    if(!secret||!isCurrent())return {blocked:true,reason:'binding_required'};
    const privateKey=await crypto.subtle.importKey('jwk',secret.privateJwk,{name:'RSASSA-PKCS1-v1_5',hash:'SHA-256'},false,['sign']);
    const sign=async text=>b64(await crypto.subtle.sign('RSASSA-PKCS1-v1_5',privateKey,new TextEncoder().encode(text)));
    let binding=secret.bindings[owner];
    if(!binding){
        if(!mayEnroll&&!canRenewBinding(secret,currentClaims))return {blocked:true,reason:'binding_required'};
        const challenge=await requestWithFallback({moduleName:'deviceSync',endpoint:'challenge',endpointIsKey:true,token:session.token,method:'post',data:{installation_uuid:secret.installationUuid,public_key:secret.publicKey}});
        if(!isCurrent())return {blocked:true};
        const response=await requestWithFallback({moduleName:'deviceSync',endpoint:'enroll',endpointIsKey:true,token:session.token,method:'post',data:{binding_uuid:challenge.data.binding_uuid,signature:await sign(challenge.data.canonical)}});
        if(!isCurrent())return {blocked:true};
        binding=response.data;secret.bindings[owner]=binding;await SecureStorage.set(keyName,secret);
    }
    if(!capability.data.execution)return {disabled:true};
    if(activeProcessor?.owner===owner)return activeProcessor.processor.pump({newCycle});
    const driver=getOfflineStorageDriver(),identity={userId:Number(currentClaims.sub),empresaId:Number(currentClaims.empresa_id),bindingUuid:binding.binding_uuid,generation:binding.generation};
    const prefix=`device-sync:${identity.empresaId}:${identity.userId}:${binding.binding_uuid}:${binding.generation}:`;
    const api=async(endpoint,uuid,data)=>{
        if(!isCurrent())throw Error('Cambió la cuenta.');
        const method=['pending','show'].includes(endpoint)?'get':'post';
        const candidate=getEndpointCandidates('deviceSync',endpoint,{isEndpointKey:true,pathParams:{uuid}})[0];
        if(!candidate||candidate.backend!=='php')throw Error('Destino de resincronización no autorizado.');
        const path=new URL(candidate.url,globalThis.location?.origin).pathname;
        const body=data===undefined?'':JSON.stringify(data),nonce=crypto.randomUUID(),at=Math.floor(Date.now()/1000);
        const proof=canonicalMerge({operation:'device_sync_api_v1',binding_uuid:binding.binding_uuid,generation:binding.generation,session_jti:currentClaims.jti,
            method:method.toUpperCase(),path,body_sha256:await mergeHash(body),nonce,at});
        const result=await requestWithFallback({moduleName:'deviceSync',endpoint,endpointIsKey:true,pathParams:{uuid},token:session.token,method,data:data===undefined?undefined:body,
            headers:{'Content-Type':'application/json','X-Sync-Binding':binding.binding_uuid,'X-Sync-Nonce':nonce,'X-Sync-Time':String(at),'X-Sync-Signature':await sign(proof)},dedupeInFlight:false});
        return result.data;
    };
    const processor=createDeviceSyncProcessor({identity,api,run,isCurrent,isOnline:()=>navigator.onLine,
        journal:{get:async uuid=>(await driver.get(OFFLINE_STORES.CONTEXTS,prefix+uuid))?.command,
            list:async()=>(await driver.getAll(OFFLINE_STORES.CONTEXTS,null,{userId:identity.userId})).filter(row=>row.id.startsWith(prefix)).map(row=>row.command),
            put:async command=>driver.put(OFFLINE_STORES.CONTEXTS,{id:prefix+command.uuid,kind:'device_sync_command',scope:prefix,userId:identity.userId,empresaId:identity.empresaId,visitaId:0,
                templateVersion:'resync_visitas_v1',version:crypto.randomUUID(),command})},
        watermark:async visits=>{
            const items=[];
            for(const entry of await driver.getAll(OFFLINE_STORES.ENTRIES,null,{userId:identity.userId})){
                if(!visits.includes(Number(entry.visitaId)))continue;
                if(!entry.payload?._merge)throw Error('El borrador histórico requiere verificar su comprobante antes de ejecutar esta solicitud.');
                const literal=buildMergeProposal(entry,entry.payload._merge,entry.revisionId,'android');
                items.push({item_key:entry.key,visita_id:Number(entry.visitaId),kind:'draft',revision_uuid:entry.revisionId,body_hash:await mergeHash(literal)});
            }
            for(const media of await driver.getAll(OFFLINE_STORES.MEDIA,null,{userId:identity.userId})){
                if(!visits.includes(Number(media.visitaId)))continue;
                const file=await driver.get(OFFLINE_STORES.FILES,media.id);
                if(!file||file.empresaId!==identity.empresaId)throw Error('El archivo conserva una intención sin comprobante verificable.');
                items.push({item_key:`file:${file.id}`,visita_id:Number(file.visitaId),kind:'file',revision_uuid:file.id,body_hash:file.sha256});
            }
            return items;
        }});
    activeProcessor={owner,processor};return processor.pump({newCycle});
}
